// Carrier detail page — FE-SCRM-030 Steps 3-11.
//
// Tabs: Contacts (default) / Activity / Plans. Tab state lives in
// ``?tab=`` so deep links and the back button preserve it. Modal
// state for add/edit/delete is owned here so the page can wire each
// surface (header buttons, contact-row actions) to the same modal
// instance.
//
// All write actions (Edit carrier, Delete carrier, Add / Edit /
// Delete contact) are gated on ``permissions.is_admin_or_owner`` ↔
// backend ``can_manage_carriers``. Plain members see the read-only
// view: no Edit/Delete buttons, no actions column on the contacts
// table.

import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Toast } from "@/design-system";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";
import { usePermissions } from "@/features/auth";

import { CarrierActivityTab } from "./CarrierActivityTab";
import { CarrierContactFormModal } from "./CarrierContactFormModal";
import { CarrierContactsTab } from "./CarrierContactsTab";
import { CarrierDetailHeader } from "./CarrierDetailHeader";
import { CarrierFormModal } from "./CarrierFormModal";
import {
  useCarrierDetailQuery,
  useCreateCarrierContactMutation,
  useDeleteCarrierContactMutation,
  useDeleteCarrierMutation,
  useUpdateCarrierContactMutation,
  useUpdateCarrierMutation,
} from "./hooks";
import {
  CarrierNotFoundError,
  type CarrierContact,
  type CarrierContactWriteInput,
  type CarrierWriteInput,
} from "./types";


type Tab = "contacts" | "activity" | "plans";
const VALID_TABS: Tab[] = ["contacts", "activity", "plans"];


export function CarrierDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const carrierId = params.carrierId;
  const permissions = usePermissions();
  const canManageCarriers = permissions.is_admin_or_owner;

  const tab: Tab = useMemo(() => {
    const raw = search.get("tab");
    if (raw && (VALID_TABS as string[]).includes(raw)) return raw as Tab;
    return "contacts";
  }, [search]);

  const detail = useCarrierDetailQuery(carrierId);

  // Modal + toast state lives here so the header / row / tab
  // surfaces all hook into one mutation cycle.
  const [editCarrierOpen, setEditCarrierOpen] = useState(false);
  const [deleteCarrierOpen, setDeleteCarrierOpen] = useState(false);
  const [contactModalState, setContactModalState] = useState<
    | { open: false }
    | { open: true; mode: "create" }
    | { open: true; mode: "edit"; contact: CarrierContact }
  >({ open: false });
  const [deleteContactState, setDeleteContactState] = useState<
    | { open: false }
    | { open: true; contact: CarrierContact }
  >({ open: false });
  const [toast, setToast] = useState<
    | null
    | { variant: "success" | "error"; title: string; description?: string }
  >(null);

  const updateCarrierMutation = useUpdateCarrierMutation();
  const deleteCarrierMutation = useDeleteCarrierMutation();
  const createContactMutation = useCreateCarrierContactMutation(carrierId ?? "");
  const updateContactMutation = useUpdateCarrierContactMutation(carrierId ?? "");
  const deleteContactMutation = useDeleteCarrierContactMutation(carrierId ?? "");

  if (!carrierId) {
    return <NotFoundView />;
  }

  if (detail.isLoading) {
    return (
      <div className="flex flex-col gap-5" data-testid="carrier-detail-page">
        <DashboardLoadingState variant="card" />
      </div>
    );
  }

  if (detail.isError) {
    if (detail.error instanceof CarrierNotFoundError) {
      return <NotFoundView />;
    }
    return (
      <div className="flex flex-col gap-5" data-testid="carrier-detail-page">
        <DashboardErrorState
          title="Couldn't load this carrier"
          retry={() => detail.refetch()}
        />
      </div>
    );
  }

  const carrier = detail.data;
  if (!carrier) return <NotFoundView />;

  function selectTab(next: Tab) {
    const params = new URLSearchParams(search);
    params.set("tab", next);
    setSearch(params, { replace: true });
  }

  function submitEditCarrier(values: CarrierWriteInput) {
    updateCarrierMutation.mutate(
      { carrier_id: carrierId!, values },
      {
        onSuccess: () => {
          setEditCarrierOpen(false);
          setToast({ variant: "success", title: "Carrier saved" });
        },
      },
    );
  }

  function submitDeleteCarrier() {
    deleteCarrierMutation.mutate(carrierId!, {
      onSuccess: () => {
        setDeleteCarrierOpen(false);
        setToast({ variant: "success", title: "Carrier deleted" });
        navigate("/dashboard/carriers", { replace: true });
      },
      onError: () => {
        setToast({
          variant: "error",
          title: "Couldn't delete this carrier",
          description: "Try again, or contact support if the problem persists.",
        });
      },
    });
  }

  function submitContact(values: CarrierContactWriteInput) {
    if (contactModalState.open && contactModalState.mode === "edit") {
      updateContactMutation.mutate(
        {
          contact_id: contactModalState.contact.carrier_contact_id,
          values,
        },
        {
          onSuccess: () => {
            setContactModalState({ open: false });
            setToast({ variant: "success", title: "Contact saved" });
          },
        },
      );
      return;
    }
    createContactMutation.mutate(values, {
      onSuccess: () => {
        setContactModalState({ open: false });
        setToast({ variant: "success", title: "Contact added" });
      },
    });
  }

  function submitDeleteContact() {
    if (!deleteContactState.open) return;
    const contactId = deleteContactState.contact.carrier_contact_id;
    deleteContactMutation.mutate(contactId, {
      onSuccess: () => {
        setDeleteContactState({ open: false });
        setToast({ variant: "success", title: "Contact deleted" });
      },
      onError: () => {
        setToast({
          variant: "error",
          title: "Couldn't delete this contact",
        });
      },
    });
  }

  const contactCount = carrier.contact_count;

  return (
    <div data-testid="carrier-detail-page" className="flex flex-col gap-5">
      <CarrierDetailHeader
        carrier={carrier}
        canManageCarriers={canManageCarriers}
        onEditCarrier={() => setEditCarrierOpen(true)}
        onDeleteCarrier={() => setDeleteCarrierOpen(true)}
        onAddContact={() =>
          setContactModalState({ open: true, mode: "create" })}
      />

      <DetailTabs currentTab={tab} onSelect={selectTab} />

      {tab === "contacts" && (
        <CarrierContactsTab
          carrierId={carrierId}
          canManageCarriers={canManageCarriers}
          onAddContact={() =>
            setContactModalState({ open: true, mode: "create" })}
          onEditContact={(c) =>
            setContactModalState({ open: true, mode: "edit", contact: c })}
          onDeleteContact={(c) =>
            setDeleteContactState({ open: true, contact: c })}
        />
      )}
      {tab === "activity" && <CarrierActivityTab carrierId={carrierId} />}
      {tab === "plans" && (
        <DashboardEmptyState
          title="Plans coming in the next release"
          description={
            "Plan inventory tied to this carrier (medical, dental, "
            + "vision plans, by Account) lands in v1.5."
          }
        />
      )}

      <CarrierFormModal
        open={editCarrierOpen}
        mode="edit"
        initial={carrier}
        isSubmitting={updateCarrierMutation.isPending}
        errorMessage={
          updateCarrierMutation.isError
            ? formatBackendError(updateCarrierMutation.error)
            : null
        }
        onClose={() => setEditCarrierOpen(false)}
        onSubmit={submitEditCarrier}
      />

      <ConfirmDialog
        open={deleteCarrierOpen}
        title={`Delete ${carrier.name}?`}
        body={
          contactCount > 0
            ? `This carrier has ${contactCount} contact${
                contactCount === 1 ? "" : "s"
              }. Deleting the carrier also removes these contacts. This cannot be undone.`
            : "This cannot be undone."
        }
        confirmLabel={
          deleteCarrierMutation.isPending ? "Deleting…" : "Delete carrier"
        }
        variant="danger"
        testId="confirm-delete-carrier"
        onConfirm={submitDeleteCarrier}
        onCancel={() => setDeleteCarrierOpen(false)}
      />

      <CarrierContactFormModal
        open={contactModalState.open}
        mode={contactModalState.open ? contactModalState.mode : "create"}
        initial={
          contactModalState.open && contactModalState.mode === "edit"
            ? contactModalState.contact
            : null
        }
        isSubmitting={
          createContactMutation.isPending || updateContactMutation.isPending
        }
        errorMessage={
          createContactMutation.isError
            ? formatBackendError(createContactMutation.error)
            : updateContactMutation.isError
              ? formatBackendError(updateContactMutation.error)
              : null
        }
        onClose={() => setContactModalState({ open: false })}
        onSubmit={submitContact}
      />

      <ConfirmDialog
        open={deleteContactState.open}
        title={
          deleteContactState.open
            ? `Delete ${deleteContactState.contact.first_name} ${deleteContactState.contact.last_name}?`
            : "Delete contact?"
        }
        body="This cannot be undone."
        confirmLabel={
          deleteContactMutation.isPending ? "Deleting…" : "Delete contact"
        }
        variant="danger"
        testId="confirm-delete-contact"
        onConfirm={submitDeleteContact}
        onCancel={() => setDeleteContactState({ open: false })}
      />

      {toast && (
        <Toast
          variant={toast.variant}
          title={toast.title}
          description={toast.description}
          onDismiss={() => setToast(null)}
        />
      )}
    </div>
  );
}


function DetailTabs({
  currentTab, onSelect,
}: { currentTab: Tab; onSelect: (next: Tab) => void }) {
  const labels: Record<Tab, string> = {
    contacts: "Contacts",
    activity: "Activity",
    plans: "Plans",
  };
  return (
    <nav
      data-testid="carrier-detail-tabs"
      role="tablist"
      className="flex gap-2 border-b border-border-subtle"
    >
      {VALID_TABS.map((t) => (
        <button
          key={t}
          type="button"
          role="tab"
          aria-selected={currentTab === t}
          data-testid={`carrier-detail-tab-${t}`}
          onClick={() => onSelect(t)}
          className={[
            "px-3 py-1.5 text-xs font-medium",
            currentTab === t
              ? "border-b-2 border-teal text-text-primary"
              : "text-text-secondary hover:text-text-primary",
          ].join(" ")}
        >
          {labels[t]}
        </button>
      ))}
    </nav>
  );
}


function NotFoundView() {
  return (
    <div className="flex flex-col gap-5" data-testid="carrier-detail-page">
      <DashboardEmptyState
        title="Carrier not found"
        description="This carrier may have been deleted or you don't have access."
      />
    </div>
  );
}


/** Best-effort surface for the backend's structured ``{detail: "..."}``
 * error body. Falls back to the message on the thrown error so a
 * network failure still produces a readable string. */
function formatBackendError(err: unknown): string {
  if (err && typeof err === "object" && "body" in err) {
    const body = (err as { body?: { detail?: string } }).body;
    if (body?.detail) {
      return humanizeBackendErrorCode(body.detail);
    }
  }
  if (err instanceof Error && err.message) return err.message;
  return "Something went wrong. Please try again.";
}


function humanizeBackendErrorCode(code: string): string {
  switch (code) {
    case "duplicate_carrier_name":
      return "A carrier with this name already exists.";
    case "name_required":
      return "Name is required.";
    case "carrier_type_invalid":
      return "Carrier type is invalid.";
    case "forbidden":
      return "You don't have permission to do that.";
    case "not_found":
      return "Not found.";
    default:
      return code.replaceAll("_", " ");
  }
}
